import { db } from './firebase';
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
} from 'firebase/firestore';

export const phraseService = {
  // Get phrases for a specific word (user or team)
  getPhrasesForWord: async (word, userId, teamId = null) => {
    const results = [];

    if (userId) {
      const ref = doc(db, 'phrases', `${word.toUpperCase()}_${userId}`);
      const snap = await getDoc(ref);
      if (snap.exists()) results.push({ ...snap.data(), source: 'personal' });
    }

    if (teamId) {
      const ref = doc(db, 'customTeamWords', `${word.toUpperCase()}_${teamId}`);
      const snap = await getDoc(ref);
      if (snap.exists()) results.push({ ...snap.data(), source: 'team' });
    }

    return results;
  },

  // Save/update a phrase for a word
  savePhrase: async (word, phrase, parts, userId, teamId = null) => {
    if (teamId) {
      const key = `${word.toUpperCase()}_${teamId}`;
      await setDoc(
        doc(db, 'customTeamWords', key),
        { word: word.toUpperCase(), phrase, parts: parts || [], updatedBy: userId, teamId, updatedAt: new Date() },
        { merge: true }
      );
    } else {
      const key = `${word.toUpperCase()}_${userId}`;
      await setDoc(
        doc(db, 'phrases', key),
        { word: word.toUpperCase(), phrase, parts: parts || [], userId, updatedAt: new Date() },
        { merge: true }
      );
    }
  },

  deletePhrase: async (word, userId, teamId = null) => {
    if (teamId) {
      await deleteDoc(doc(db, 'customTeamWords', `${word.toUpperCase()}_${teamId}`));
    } else {
      await deleteDoc(doc(db, 'phrases', `${word.toUpperCase()}_${userId}`));
    }
  },

  // Get all phrases for a user (for study)
  getUserPhrases: async (userId) => {
    const q = query(
      collection(db, 'phrases'),
      where('userId', '==', userId),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  // Get all phrases for a team
  getTeamPhrases: async (teamId) => {
    const q = query(
      collection(db, 'customTeamWords'),
      where('teamId', '==', teamId)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  /**
   * Returns a map { WORD: { phrase?, parts?, skip?, updatedBy? } } for a team.
   * Single query, O(n) local transform.
   */
  getTeamWordStatuses: async (teamId) => {
    const q = query(collection(db, 'customTeamWords'), where('teamId', '==', teamId));
    const snap = await getDocs(q);
    const map = {};
    snap.docs.forEach((d) => {
      const data = d.data();
      map[data.word?.toUpperCase()] = data;
    });
    return map;
  },

  /**
   * Marks a word as "skip" for the team (yellow state).
   * If already skipped, calling again un-skips it.
   */
  markWordAsSkip: async (teamId, word, userId, currentlySkipped = false) => {
    const key = `${word.toUpperCase()}_${teamId}`;
    if (currentlySkipped) {
      // Remove skip flag — delete doc if no phrase either
      await setDoc(
        doc(db, 'customTeamWords', key),
        { word: word.toUpperCase(), teamId, skip: false, updatedBy: userId, updatedAt: new Date() },
        { merge: true }
      );
    } else {
      await setDoc(
        doc(db, 'customTeamWords', key),
        { word: word.toUpperCase(), teamId, skip: true, updatedBy: userId, updatedAt: new Date() },
        { merge: true }
      );
    }
  },

  // Get study progress (flashcard memory)
  getStudyProgress: async (userId) => {
    const ref = doc(db, 'studyProgress', userId);
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data() : { remembered: {}, forgotten: {} };
  },

  saveFlashcardResult: async (userId, word, remembered) => {
    const ref = doc(db, 'studyProgress', userId);
    const field = remembered ? 'remembered' : 'forgotten';
    await setDoc(
      ref,
      {
        [field]: { [word]: Date.now() },
      },
      { merge: true },
    );
  },
};
